/**
 * La scheda dell'elemento: un palo, e tutto cio' che lo riguarda in un posto.
 *
 * **Perche' esiste.** Il banco mostra un candidato per volta e la copertura
 * mostra la linea intera: in mezzo mancava la domanda che l'operatore si fa
 * piu' spesso — «che cos'altro c'e' su questo palo?». Un secondo candidato
 * sullo stesso isolatore, un fotogramma laterale che smentisce quello
 * frontale, la stessa constatazione gia' scritta nel volo dell'anno scorso:
 * senza la scheda si scoprono cambiando schermata, e spesso non si scoprono.
 *
 * **Sola lettura.** Qui non si decide: il verdetto vuole un lease, e il lease
 * sta nel banco. La scheda dice, e il collegamento in fondo riporta al
 * candidato da cui si e' arrivati — con lo stato dell'indirizzo intatto, cosi'
 * la sessione non si perde per aver guardato.
 */
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { ServiceError } from '../api'
import { StatusPill, type PillTone } from '../carta/StatusPill'
import { QueueRow, QueueTable } from '../carta/QueueTable'
import { Margin, Severity } from '../design/indicators'
import { decimal, day, metres, moment, notMeasured } from '../format'
import { readElement, type ElementCandidate, type ElementSheet } from '../elements'
import { explainService } from '../i18n/explain'
import { useT } from '../i18n/LocaleProvider'
import type { MessageKey } from '../i18n/messages'
import type { CoverageStatus } from '../types'

export const SCHEDA = 'modulo-elemento'

const STATO: Record<CoverageStatus, { label: MessageKey; tone: PillTone }> = {
  covered: { label: 'coverage.statusCovered', tone: 'sev-0' },
  declared: { label: 'coverage.statusDeclared', tone: 'sev-2' },
  gap: { label: 'coverage.statusGap', tone: 'sev-4' },
}

const VERDETTI: Record<string, MessageKey> = {
  confirmed: 'element.confirmed',
  rejected: 'element.rejected',
  withdrawn: 'element.withdrawn',
}

/** Il candidato da cui si e' arrivati, se l'indirizzo lo porta: e' quello a
 *  cui il collegamento di ritorno riporta. Un gettone solo, senza spazi. */
export function parseCandidateOrigin(search: string): string | null {
  const value = new URLSearchParams(search).get('candidate')
  return value !== null && /^[A-Za-z0-9._~-]+$/.test(value) ? value : null
}

/** Lo stato dell'indirizzo del banco, senza il candidato: e' cio' che si
 *  restituisce al ritorno, perche' la sessione di verifica sta li' dentro. */
function backQuery(search: string): string {
  const kept = new URLSearchParams(search)
  kept.delete('candidate')
  const suffix = kept.toString()
  return suffix === '' ? '' : `?${suffix}`
}

export function InspectionElement({
  inspectionId,
  elementId,
}: {
  inspectionId: string
  elementId: string
}): JSX.Element {
  const t = useT()
  const location = useLocation()
  const [sheet, setSheet] = useState<ElementSheet | null>(null)
  const [failure, setFailure] = useState<ServiceError | null>(null)

  useEffect(() => {
    let cancelled = false
    setSheet(null)
    setFailure(null)
    void readElement(inspectionId, elementId)
      .then((found) => {
        if (!cancelled) setSheet(found)
      })
      .catch((error: unknown) => {
        if (!cancelled && error instanceof ServiceError) setFailure(error)
      })
    return () => {
      cancelled = true
    }
  }, [inspectionId, elementId])

  if (failure !== null) {
    return (
      <main className="scheda">
        <h2>{t('title.element')}</h2>
        <p role="alert">{explainService(t, failure.code, failure.message)}</p>
      </main>
    )
  }
  if (sheet === null) {
    return (
      <main className="scheda">
        <p role="status">{t('element.reading')}</p>
      </main>
    )
  }

  const origin = parseCandidateOrigin(location.search)
  const back =
    origin === null
      ? `/inspections/${inspectionId}/queue${backQuery(location.search)}`
      : `/inspections/${inspectionId}/candidates/${origin}${backQuery(location.search)}`
  const stato = STATO[sheet.status]

  return (
    <main className="scheda">
      <header className="scheda-testa">
        <h2>
          {t('element.heading', { type: sheet.element_type, id: sheet.external_element_id })}
        </h2>
        <StatusPill label={t(stato.label)} tone={stato.tone} />
      </header>

      <dl className="scheda-anagrafica">
        {sheet.line_code !== null && (
          <>
            <dt>{t('element.line')}</dt>
            <dd>
              <span className="t-num">{sheet.line_code}</span>
              {sheet.line_description !== null && <span className="tenue"> · {sheet.line_description}</span>}
            </dd>
          </>
        )}
        {sheet.code !== null && (
          <>
            <dt>{t('element.code')}</dt>
            <dd>
              <span className="t-num">{sheet.code}</span>
              {sheet.description !== null && <span className="tenue"> · {sheet.description}</span>}
            </dd>
          </>
        )}
        {sheet.end_nodes.length > 0 && (
          <>
            <dt>{t('element.endNodes')}</dt>
            <dd className="t-num">{sheet.end_nodes.join(' – ')}</dd>
          </>
        )}
        {sheet.latitude !== null && sheet.longitude !== null && (
          <>
            <dt>{t('element.position')}</dt>
            <dd className="t-num">
              {decimal(sheet.latitude, 5)}, {decimal(sheet.longitude, 5)}
            </dd>
          </>
        )}
        {sheet.not_inspected_reason !== null && (
          <>
            <dt>{t('element.notInspected')}</dt>
            <dd>
              {sheet.not_inspected_reason}
              {sheet.not_inspected_note !== null && <span className="tenue"> · {sheet.not_inspected_note}</span>}
            </dd>
          </>
        )}
      </dl>

      <section aria-label={t('element.frames')}>
        <h3>
          {t('element.frames')} <span className="t-num">{decimal(sheet.frames.length)}</span>
        </h3>
        {sheet.frames.length === 0 ? (
          <p className="tenue">{t('element.noFrames')}</p>
        ) : (
          <ul className="scheda-fotogrammi">
            {sheet.frames.map((one) => (
              <li key={one.photo_id} className={one.primary ? 'principale' : undefined}>
                {one.thumbnail_url !== null ? (
                  <img src={one.thumbnail_url} alt={one.filename} loading="lazy" />
                ) : (
                  <div className="scheda-senza-anteprima" aria-hidden="true" />
                )}
                <span className="t-num">{one.filename}</span>
                <span className="tenue">
                  {one.taken_at !== null ? moment(one.taken_at) : ''}
                  {one.distance_m !== null ? ` · ${metres(one.distance_m)}` : ''}
                </span>
                <span className="tenue">
                  {t('element.frameCounts', { candidates: one.candidates, findings: one.findings })}
                  {one.reviewed ? ` · ${t('element.frameReviewed')}` : ''}
                  {one.primary ? ` · ${t('element.framePrimary')}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label={t('element.candidates')}>
        <h3>
          {t('element.candidates')} <span className="t-num">{decimal(sheet.candidates.length)}</span>
        </h3>
        {sheet.candidates.length === 0 ? (
          <p className="tenue">{t('element.noCandidates')}</p>
        ) : (
          <div className="tabella-scorrevole">
            <QueueTable>
              <div className="queue-row scheda-intestazione">
                <span className="t-label">{t('element.colCode')}</span>
                <span className="t-label">{t('element.colLevel')}</span>
                <span className="t-label">{t('element.colMargin')}</span>
                <span className="t-label">{t('element.colFrame')}</span>
                <span className="t-label">{t('element.colVerdict')}</span>
                <span className="t-label">{t('element.colOpen')}</span>
              </div>
              {sheet.candidates.map((one) => (
                <QueueRow key={one.detection_id} selected={one.detection_id === origin}>
                  <span>
                    <span className="t-num">{one.code}</span>
                    {one.description !== '' && <span className="tenue"> · {one.description}</span>}
                  </span>
                  <Severity level={one.final_level ?? one.level} />
                  <Margin measured={one.measured_distance_m} minimum={one.minimum_distance_m} />
                  <span className="t-num scheda-nome" title={one.photo_filename ?? undefined}>
                    {one.photo_filename ?? notMeasured()}
                  </span>
                  <Verdict candidate={one} />
                  <Link
                    to={`/inspections/${inspectionId}/candidates/${one.detection_id}${backQuery(location.search)}`}
                  >
                    {t('element.openCandidate')}
                  </Link>
                </QueueRow>
              ))}
            </QueueTable>
          </div>
        )}
      </section>

      <section aria-label={t('element.findings')}>
        <h3>
          {t('element.findings')} <span className="t-num">{decimal(sheet.findings.length)}</span>
        </h3>
        {sheet.findings.length === 0 ? (
          <p className="tenue">{t('element.noFindings')}</p>
        ) : (
          <ul className="scheda-elenco">
            {sheet.findings.map((one) => (
              <li key={one.finding_id}>
                <Severity level={one.level} />{' '}
                <span className="t-num">{one.code}</span>
                {one.description !== '' && <span className="tenue"> · {one.description}</span>}
                <span className="tenue"> · {moment(one.created_at)}</span>
                {one.note !== null && <span className="tenue"> · {one.note}</span>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label={t('element.history')}>
        <h3>{t('element.history')}</h3>
        {sheet.history.length === 0 ? (
          <p className="tenue">{t('element.noHistory')}</p>
        ) : (
          <ul className="scheda-elenco">
            {sheet.history.map((one) => (
              <li key={one.inspection_id}>
                <Link to={`/inspections/${one.inspection_id}/elements/${one.element_id}`}>
                  {t('element.pastFlight', {
                    id: one.external_inspection_id,
                    when: day(one.created_at),
                  })}
                </Link>
                <span className="tenue">
                  {' · '}
                  {t('element.pastCounts', { frames: one.frames, findings: one.findings })}
                  {' · '}
                  {one.state}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <p>
        <Link className="azione primaria primario" to={back}>
          {origin === null ? t('element.backToQueue') : t('element.backToCandidate')}
        </Link>
        <Link className="azione" to={`/inspections/${inspectionId}/coverage`}>
          {t('element.openCoverage')}
        </Link>
      </p>
    </main>
  )
}

/** Il verdetto sul candidato, con chi e quando: senza, la scheda direbbe che
 *  cosa il modello ha proposto e non che cosa una persona ha deciso. */
function Verdict({ candidate }: { candidate: ElementCandidate }): JSX.Element {
  const t = useT()
  if (candidate.decision === null) return <span className="tenue">{t('element.undecided')}</span>
  const label = VERDETTI[candidate.decision]
  return (
    <span>
      {label === undefined ? candidate.decision : t(label)}
      {candidate.reason_code !== null && <span className="tenue"> · {candidate.reason_code}</span>}
      {candidate.reviewer !== null && <span className="tenue"> · {candidate.reviewer}</span>}
      {candidate.decided_at !== null && (
        <span className="tenue"> · {moment(candidate.decided_at)}</span>
      )}
    </span>
  )
}
